import React from 'react';
import {
  formatCartLineFormulaLabel,
  formatCartLineHostQtyLabel,
  normalizeCartLineAddOns,
} from '../pages/OurMenu/cartAddOnUtils';
import './CartLineAddOns.css';

/**
 * Price breakdown under a host line — only when add-ons exist.
 *
 * - `summary` (checkout/payment): Qty N + muted amounts
 * - `formula` (cart): "Name (qty × ₹unit)" + total
 *
 * @param {{
 *   addOns?: Array<{ id?: number; name: string; price: number; quantity?: number }>,
 *   className?: string,
 *   hostQty?: number,
 *   hostUnitPrice?: number,
 *   hostName?: string,
 *   variant?: 'summary' | 'formula',
 * }} props
 */
export default function CartLineAddOns({
  addOns,
  className = '',
  hostQty,
  hostUnitPrice,
  hostName = '',
  variant = 'summary',
}) {
  const rows = normalizeCartLineAddOns(addOns);
  if (!rows.length) return null;

  const qty = Math.max(1, Math.round(Number(hostQty) || 1));
  const unit = Number(hostUnitPrice);
  const showHost = Number.isFinite(unit) && unit >= 0;
  const hostTotal = showHost ? unit * qty : 0;
  const formula = variant === 'formula';

  const hostLabel = !showHost
    ? ''
    : formula
      ? formatCartLineFormulaLabel(hostName || 'Item', qty, unit)
      : formatCartLineHostQtyLabel(qty);

  const rootClass = [
    'cart-line-addons',
    formula ? 'cart-line-addons--formula' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      {showHost ? (
        <div className="cart-line-addons__host">
          <span className="cart-line-addons__host-label">{hostLabel}</span>
          <span className="cart-line-addons__host-total">₹{hostTotal.toFixed(2)}</span>
        </div>
      ) : null}

      <ul className="cart-line-addons__list">
        {rows.map((addon) => {
          const addonQty = Math.max(1, Number(addon.quantity) || 1);
          const addonUnit = Number(addon.price || 0);
          const lineTotal = addonUnit * addonQty;
          return (
            <li key={addon.id ?? addon.name} className="cart-line-addons__item">
              <span className="cart-line-addons__label">
                {formula ? null : (
                  <span className="cart-line-addons__plus" aria-hidden>
                    +
                  </span>
                )}
                <span className="cart-line-addons__name">
                  {formula
                    ? formatCartLineFormulaLabel(addon.name, addonQty, addonUnit)
                    : `${addon.name}${addonQty > 1 ? ` × ${addonQty}` : ''}`}
                </span>
              </span>
              <span className="cart-line-addons__total">₹{lineTotal.toFixed(2)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
